import { api, type SessionInfo, type ThreadSummary } from './api.js';
import { Router } from './router.js';
import { renderShellNav } from './dom.js';
import { installErrorReporter } from './error-reporter.js';

/**
 * App-Shell-Boot: eine HTML-Seite (/app-shell), der Router montiert Views.
 * Page-Module werden lazy via dynamic import geladen — pro View ein Chunk.
 */

declare global {
  interface Window {
    __appRouter?: Router;
  }
}

async function boot(): Promise<void> {
  // Error-Tracking zuerst: ab jetzt geht jede unbehandelte Exception an
  // POST /api/errors (Offline-Queue inklusive).
  installErrorReporter();
  // Eigenbau-Analytics (18.09.: GA4/LinkedIn raus, Self-Hosted ohne
  // Dritt-Cookies): pageview je Mount — UTM-Capture macht tracking-boot.js.
  void import('./tracking.js').then(({ initTracking }) => initTracking());
  // PWA-Install-CTA im Footer (Saso 24.09.): zeigt sich nur, wenn der Browser
  // beforeinstallprompt gefeuert hat und die App noch nicht installiert ist.
  // Auf jeder Seite mit Footer aktiv (Landing UND App) — das Modul guardet
  // selbst, wenn kein Button im DOM ist.
  void import('./pwa-install.js').then(({ initPwaInstall }) => initPwaInstall());
  const page = document.body.dataset.page;
  // Scroll-nach-oben (Saso-Plan 01.10.): Floating-Button ab 600 px Scroll —
  // lange Analysen-Berichte brauchen einen kurzen Weg zurueck nach oben.
  if (page === 'app') void import('./scroll-top.js').then(({ initScrollTop }) => initScrollTop());

  const logout = document.getElementById('logout');
  logout?.addEventListener('click', async () => {
    await api.post('/api/auth/logout');
    location.href = '/';
  });

  // Landing bleibt eine eigene Seite (Marketing-Landing mit Cast-Choice).
  if (page === 'landing') {
    const { initLanding } = await import('./pages/landing.js');
    await initLanding();
    return;
  }

  if (page !== 'app') return;

  // SPA-Shell: Router aufbauen, Session einmal laden, Views lazy laden.
  const outlet = document.getElementById('outlet');
  if (!outlet) return;

  const router = new Router(outlet);
  window.__appRouter = router;

  // App:navigate-Event: Views navigieren ohne Router-Import-Zirkularität.
  window.addEventListener('app:navigate', (event) => {
    const detail = (event as CustomEvent<string>).detail;
    if (typeof detail === 'string') router.navigate(detail);
  });

  // app:login-Event: Login-View hat erfolgreich authentifiziert — neu
  // rendern (Session ist jetzt gesetzt), gewuenschte Route aus ?next= oder
  // Fallback /app.
  window.addEventListener('app:login', (event) => {
    const next = (event as CustomEvent<string | undefined>).detail;
    void refreshAfterLogin(next || '/app');
  });

  router.add('/app', () => import('./views/auftrag.js'), { requiresAuth: true });
  // Alias: /app/auftrag war bis 15.09.2026 die verlinkte URL des
  // Referenz-Buttons und ist evtl. geb/bookmarkt — gleiche View.
  router.add('/app/auftrag', () => import('./views/auftrag.js'), { requiresAuth: true });
  router.add('/app/login', () => import('./views/login.js'));
  router.add('/app/analysen', () => import('./views/analysen.js'), { requiresAuth: true });
  router.add('/app/thread', () => import('./views/thread.js'), { requiresAuth: true });
  router.add('/app/account', () => import('./views/account.js'), { requiresAuth: true });
  router.add('/app/prozesse', () => import('./views/prozesse.js'), { requiresAuth: true });

  try {
    router.session = await api.get<SessionInfo>('/api/session');
  } catch {
    router.session = null;
  }
  // Topbar-Nav passend zur Session rendern: Anonym -> Anmelden-Link;
  // Auth -> Auftrag / Analysen / Konto + Abmelden.
  const shellNav = document.querySelector<HTMLElement>('[data-shell-nav]');
  if (shellNav) renderShellNav(shellNav, router.session);
  router.start();

  // Login-Helfer: holt die Session neu und navigiert auf die gewünschte
  // Route (oder Fallback /app). Wird vom app:login-Event der Login-View
  // ausgelöst, sobald das Magic-Link-Cookie gesetzt ist.
  async function refreshAfterLogin(target: string): Promise<void> {
    try {
      router.session = await api.get<SessionInfo>('/api/session');
    } catch {
      router.session = null;
    }
    if (router.session?.authenticated) {
      // Topbar neu rendern, jetzt mit vollstaendiger Navigation.
      const shellNav = document.querySelector<HTMLElement>('[data-shell-nav]');
      if (shellNav) renderShellNav(shellNav, router.session);
      // Vor dem Navigieren in die History zurücksetzen, damit Back nicht
      // wieder auf /login zeigt.
      history.replaceState(null, '', target);
      router.navigate(target, true);
    }
  }

  // Shell-Tint: Saso 24.09.: 'alle seiten (ausser die auftragsseite)
  // wieder einfarbig'. Wir lassen den Tint nur auf der Auftragsseite
  // (auftrag.ts setzt ihn selbst, sobald ein Team gewaehlt ist). Andere
  // Views (Analysen, Thread, Account, Login) bleiben neutral. Kontext
  // 'welches Team laeuft gerade' kommt jetzt aus der Analysen-Karten-
  // Farbe (linker Rand pro Team) — derselbe Effekt, gezielter.


  // Die Session wurde beim Boot einmal geholt. Sie kann ablaufen, waehrend die
  // Shell offen ist — das faengt jetzt api.ts zentral ab (401 'unauthenticated'
  // -> Weiterleitung zur Anmeldung).
  //
  // Vorher stand hier ein Wrapper um router.navigate, der vor JEDEM
  // Seitenwechsel /api/session geholt hat. Der View-Chunk begann erst danach zu
  // laden — ein zusaetzlicher Round-Trip auf jedem Klick, fuer einen Fall, der
  // fast nie eintritt.
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Verzögert etwas, damit der initiale Render nicht ausgebremst wird.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => listenForUpdates(reg))
      .catch(() => {
        /* Offline-Betrieb ist optional — ein Fehler darf die App nicht blockieren. */
      });
  });
}

function listenForUpdates(reg: ServiceWorkerRegistration): void {
  // Bereits wartender neuer SW direkt aktivieren.
  if (reg.waiting) promptUpdate(reg.waiting);
  reg.addEventListener('updatefound', () => {
    const nw = reg.installing;
    if (!nw) return;
    nw.addEventListener('statechange', () => {
      if (nw.state === 'installed' && navigator.serviceWorker.controller) {
        promptUpdate(nw);
      }
    });
  });
  // Tab offen lassen — gelegentlich nachfragen, sonst bleiben manche Tabs ewig
  // auf der alten Version hängen.
  setInterval(() => {
    void reg.update();
  }, 10 * 60 * 1000);
}

function promptUpdate(worker: ServiceWorker): void {
  // Auto-Update: nicht fragen, einfach übernehmen. Wenn der User offline
  // arbeitet, entscheidet der Browser ohnehin ohne uns.
  worker.postMessage('SKIP_WAITING');
}

let refreshing = false;
function watchControllerChange(): void {
  if (!('serviceWorker' in navigator)) return;
  // Beim allerersten Besuch uebernimmt der frisch installierte Service Worker
  // per clients.claim() die Kontrolle — das ist kein Update, sondern die
  // Erstinstallation. Ohne diese Pruefung laedt die Seite Sekunden nach dem
  // ersten Render neu und verwirft, was der Besucher schon getippt hat
  // (reproduzierbar im Anmeldeformular).
  const hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || refreshing) return;
    refreshing = true;
    // Einmal hart nachladen, damit der neue SW die Kontrolle übernimmt.
    location.reload();
  });
}

watchControllerChange();
registerServiceWorker();
void boot();
